import { Outlet, useLocation, useNavigate } from 'react-router';
import { Building2 } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { CommandPalette } from './CommandPalette';
import { ReservationDrawer } from '@/components/reservations/ReservationDrawer';
import { LgpdBanner } from '@/components/LgpdBanner';
import { TrialBanner } from '@/components/TrialBanner';
import { useAuthStore } from '@/store/auth.store';
import { useTenant } from '@/hooks/useTenants';
import { isSystemOwner } from '@/lib/authz';
import { MasterTenantScopeBar } from './MasterTenantScopeBar';
import { isMasterTenantScopedRoute } from './masterTenantScope';
import { SpotlightTour } from '@/components/onboarding-tour/SpotlightTour';
import { useTourSteps } from '@/components/onboarding-tour/useTourSteps';
import { useMarkTutorialSeen } from '@/components/onboarding-tour/useMarkTutorialSeen';

export function MainLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = useAuthStore((s) => s.user);
  const isImpersonating = useAuthStore((s) => s.isImpersonating);
  const tenantId = useAuthStore((s) => s.tenantId);
  const { data: tenant } = useTenant(tenantId ?? 0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedReservationId, setSelectedReservationId] = useState<string | null>(null);
  const [commandOpen, setCommandOpen] = useState(false);
  const needsMasterTenant = isSystemOwner(user) && !isImpersonating && isMasterTenantScopedRoute(pathname) && !tenantId;
  const { steps: tourSteps, shouldAutoStart } = useTourSteps();
  const markTutorialSeen = useMarkTutorialSeen();
  const markTutorialSeenMutate = markTutorialSeen.mutate;
  // Identidade estável: sem isso, cada render de MainLayout cria uma função nova,
  // o que faz o efeito de assinatura do SpotlightTour (deps [on, onFinish, ...])
  // re-rodar a cada render em vez de só uma vez por tour.
  const handleTourFinish = useCallback(() => {
    markTutorialSeenMutate();
  }, [markTutorialSeenMutate]);
  const handleSidebarExpandRequired = useCallback(() => {
    setSidebarCollapsed(false);
  }, []);

  // Cmd+K / Ctrl+K global shortcut
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setCommandOpen((open) => !open);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const handleViewReservation = (id: string) => {
    setSelectedReservationId(id);
    setDrawerOpen(true);
  };

  return (
    <div className="innsight-shell innsight-theme-root min-h-screen overflow-hidden bg-background text-foreground transition-colors duration-200">
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        mobileOpen={mobileNavigationOpen}
        onMobileClose={() => setMobileNavigationOpen(false)}
      />

      <div
        className={`relative min-h-screen transition-all duration-300 ease-out ${
          sidebarCollapsed ? 'md:ml-18' : 'md:ml-65'
        }`}
      >
        <Topbar
          onNewReservation={() => navigate('/reservas?new=true')}
          onOpenCommand={() => setCommandOpen(true)}
          onToggleNavigation={() => setMobileNavigationOpen((open) => !open)}
        />
        <MasterTenantScopeBar />

        <TrialBanner trialEndsAt={(tenant as unknown as { trial_ends_at?: string })?.trial_ends_at} />
        <LgpdBanner />

        <main className="innsight-workspace min-h-[calc(100vh-4rem)] p-4 md:p-7">
          {needsMasterTenant ? <div className="mx-auto mt-16 max-w-xl rounded-2xl border border-primary/20 bg-card p-8 text-center shadow-sm"><div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary"><Building2 className="h-6 w-6" /></div><h1 className="mt-4 text-xl font-bold">Selecione o hotel que deseja consultar</h1><p className="mt-2 text-sm leading-6 text-muted-foreground">O perfil Allmight continua na administração central. Use o seletor acima para carregar somente os dados operacionais desta tela, sem impersonar o cliente.</p></div> : <Outlet context={{ onViewReservation: handleViewReservation }} />}
        </main>
      </div>

      <CommandPalette open={commandOpen} onOpenChange={setCommandOpen} />
      <ReservationDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        reservationId={selectedReservationId}
      />

      {shouldAutoStart ? (
        <SpotlightTour
          steps={tourSteps}
          onFinish={handleTourFinish}
          onRequireSidebarExpanded={handleSidebarExpandRequired}
        />
      ) : null}
    </div>
  );
}

export default MainLayout;
